import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import CyberMemorialService from '../../services/FamilyPlaza/CyberMemorialService';

const MemorialAlbumEditModal = ({ isOpen, onClose, onConfirm, albumData }) => {
  const [albumTitle, setAlbumTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  // Helper function to convert image URL to base64
  const convertImageUrlToBase64 = async (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Enable CORS for external images
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        ctx.drawImage(img, 0, 0);
        
        try {
          const base64 = canvas.toDataURL('image/jpeg', 0.8); // Convert to base64 with 80% quality
          resolve(base64);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = imageUrl;
    });
  };

  // Initialize form with current album data
  useEffect(() => {
    if (albumData && isOpen) {
      setAlbumTitle(albumData.title || '');
      setPreviewUrl(albumData.image || null);
      setSelectedFile(null);
      setError('');
    }
  }, [albumData, isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setError('');
    }
  };

  const handleConfirm = async () => {
    if (!albumTitle.trim()) {
      setError('앨범 제목을 입력해주세요.');
      return;
    }

    setIsUpdating(true);
    setError('');

    try {
      let base64Image = '';
      
      if (selectedFile) {
        // Convert new file to base64
        base64Image = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile);
        });
      } else if (albumData && albumData.image) {
        // Convert existing image URL to base64
        try {
          base64Image = await convertImageUrlToBase64(albumData.image);
        } catch (conversionError) {
          console.error('Error converting existing image to base64:', conversionError);
          setError('기존 이미지를 처리하는 중 오류가 발생했습니다.');
          setIsUpdating(false);
          return;
        }
      }

      const updateData = {
        albumId: albumData.albumId,
        albumTitle: albumTitle.trim(),
        base64Image: base64Image
      };

      const response = await CyberMemorialService.updateMemorialAlbum(updateData);
      
      if (response) {
        // Call onConfirm with updated data
        onConfirm(response);
      }
    } catch (error) {
      console.error('Error updating memorial album:', error);
      setError('앨범 수정에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setAlbumTitle('');
    setSelectedFile(null);
    setPreviewUrl(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="memorial-album-edit-modal-overlay">
      <div className="memorial-album-edit-modal">
        <div className="memorial-album-edit-modal-header">
          <h2 className="memorial-album-edit-modal-title">추모앨범 수정</h2>
          <button className="memorial-album-edit-modal-close" onClick={handleClose}>
            <FiX />
          </button>
        </div>
        
        <div className="memorial-album-edit-modal-content">
          <div className="memorial-album-edit-modal-field">
            <label className="memorial-album-edit-modal-label">앨범 제목</label>
            <input
              type="text"
              className="memorial-album-edit-modal-input"
              value={albumTitle}
              onChange={(e) => setAlbumTitle(e.target.value)}
              placeholder="앨범 제목을 입력해주세요"
              maxLength={100}
            />
          </div>
          
          <div className="memorial-album-edit-modal-field">
            <label className="memorial-album-edit-modal-label">사진</label>
            <div className="memorial-album-edit-modal-file-input">
              <input
                type="file"
                id="memorial-album-edit-file"
                className="memorial-album-edit-modal-file-input-hidden"
                onChange={handleFileChange}
                accept="image/*"
              />
              <label htmlFor="memorial-album-edit-file" className="memorial-album-edit-modal-file-label">
                <span className="memorial-album-edit-modal-file-text">
                  {selectedFile ? selectedFile.name : '새로운 사진을 선택해주세요 (선택사항)'}
                </span>
                <div className="memorial-album-edit-modal-file-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 7V5C3 3.89543 3.89543 3 5 3H8.5L10.5 5H19C20.1046 5 21 5.89543 21 7V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7Z" stroke="#666" strokeWidth="2" fill="none"/>
                    <circle cx="11" cy="11" r="3" stroke="#666" strokeWidth="2" fill="none"/>
                    <path d="M21 21L16.65 16.65" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </label>
            </div>
          </div>
          
          {previewUrl && (
            <div className="memorial-album-edit-modal-preview">
              <img src={previewUrl} alt="Preview" />
            </div>
          )}
          
          {error && (
            <div className="memorial-album-edit-modal-error">
              <p>{error}</p>
            </div>
          )}
        </div>
        
        <div className="memorial-album-edit-modal-footer">
          <button className="memorial-album-edit-modal-cancel-btn" onClick={handleClose}>
            취소
          </button>
          <button 
            className="memorial-album-edit-modal-update-btn" 
            onClick={handleConfirm} 
            disabled={isUpdating}
          >
            {isUpdating ? '수정 중...' : '수정'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemorialAlbumEditModal;
